import { RefObject, useEffect } from 'react';

/**
 * Calls `onOutside` when a `mousedown` occurs outside the referenced element.
 *
 * Use the same shape that already existed across ~12 popovers/menus: register
 * only while `enabled` is true, ignore events that originated inside `ref`.
 *
 * @param ref - Ref to the container element. The hook is a no-op while it is null.
 * @param onOutside - Called with the original event when a mousedown lands outside.
 * @param enabled - When false, no listener is attached. Defaults to true.
 */
export function useClickOutside<T extends HTMLElement>(
  ref: RefObject<T | null>,
  onOutside: (event: MouseEvent) => void,
  enabled: boolean = true,
): void {
  useEffect(() => {
    if (!enabled) return;

    const handleMouseDown = (event: MouseEvent) => {
      const el = ref.current;
      if (el && !el.contains(event.target as Node)) {
        onOutside(event);
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [ref, onOutside, enabled]);
}
